import { memo, useCallback } from 'react';

import {
  BulkEditSingleSelectCustomFieldModal,
  type BulkEditSingleSelectCustomFieldModalProps,
} from '~/components/Modals/BulkEditCustomFieldModal/BulkEditSingleSelectCustomFieldModal';
import {
  type ResolveInitialValueFn,
  useSelectAllCustomFieldModal,
} from '~/hooks/customFields/useSelectAllCustomFieldModal';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetPrivateCustomFieldOptions } from '~/swr-hooks/customFields/useGetPrivateCustomFieldOptions';
import { useUpdateItemsCustomFieldValue } from '~/utils/CustomFields/useUpdateItemsCustomFieldValue';

export type PrivateBulkEditSingleSelectCustomFieldModalProps = Omit<
  BulkEditSingleSelectCustomFieldModalProps,
  'cfOptions' | 'onSave'
>;

const resolveInitialValue: ResolveInitialValueFn = (common, field, cfOptions) => {
  const selectedValueId = common.value && 'valueId' in common.value ? common.value.valueId : null;
  const selectedValue = selectedValueId ? cfOptions?.find((option) => option.id === selectedValueId) : null;

  return {
    ...field,
    value: selectedValue || null,
    values: null,
    plainTextValue: undefined,
    dateValue: undefined,
  };
};

export const PrivateBulkEditSingleSelectCustomFieldModal = memo(
  (props: AirModalProps<PrivateBulkEditSingleSelectCustomFieldModalProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceId = currentWorkspace?.id;
    const { updateItemsCustomFieldValue } = useUpdateItemsCustomFieldValue();

    const { data: cfOptions } = useGetPrivateCustomFieldOptions({
      customFieldId: props.field.id,
    });

    const { selectAll, initialValues, initialStatus, isLoadingInitialValues, handleSelectAllSave } =
      useSelectAllCustomFieldModal({
        field: props.field,
        fallbackInitialValues: props.initialValues,
        resolveInitialValue,
        cfOptions: cfOptions?.data,
      });

    const onSave = useCallback<BulkEditSingleSelectCustomFieldModalProps['onSave']>(
      async (params) => {
        if (selectAll) {
          await handleSelectAllSave({
            baseCustomField: params.baseCustomField,
            operation: 'singleSelect',
            trackLocation: 'context-menu-bulk-edit',
            valueId: params.nextValue?.id ?? null,
          });
          return;
        }

        await updateItemsCustomFieldValue({
          ...params,
          trackingLocation: 'context-menu-bulk-edit',
          workspaceId,
        });
      },
      [handleSelectAllSave, selectAll, updateItemsCustomFieldValue, workspaceId],
    );

    return (
      <BulkEditSingleSelectCustomFieldModal
        {...props}
        initialValues={initialValues}
        initialStatus={initialStatus}
        isLoadingInitialValues={isLoadingInitialValues}
        cfOptions={cfOptions}
        onSave={onSave}
      />
    );
  },
);

PrivateBulkEditSingleSelectCustomFieldModal.displayName = 'PrivateBulkEditSingleSelectCustomFieldModal';
